import { useState, useEffect } from 'react';
import { DateRangePicker } from 'rsuite';
import { Sparkles, TrendingUp, Zap, AlertCircle, Users, Bot, DollarSign, Activity, RefreshCw } from 'lucide-react';
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip as ChartTooltip,
    Legend as ChartLegend
} from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import { aiAPI } from '../services/api';
import { useTheme } from '../context/ThemeContext';

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Filler, ChartTooltip, ChartLegend);

// Formato compacto para numeros grandes (KPIs): 1.2K, 3.4M, 1.5B
const formatNumCompact = (n) => {
    const num = Number(n || 0);
    if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    return num.toLocaleString('es-PE');
};

const formatLocalDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const MetricasIA = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    // Colores adaptables para Chart.js
    const axisColor     = isDark ? 'rgba(255,255,255,0.5)'   : 'rgba(15,23,42,0.6)';
    const gridColor     = isDark ? 'rgba(255,255,255,0.05)'  : 'rgba(15,23,42,0.08)';
    const tooltipBg     = isDark ? 'rgba(20,30,60,0.95)'     : 'rgba(255,255,255,0.98)';
    const tooltipTitle  = isDark ? '#fff'                    : '#0f172a';
    const tooltipBody   = isDark ? 'rgba(255,255,255,0.85)'  : 'rgba(15,23,42,0.75)';
    const tooltipBorder = isDark ? 'rgba(140,220,255,0.3)'   : 'rgba(15,23,42,0.12)';
    const legendColor   = isDark ? 'rgba(255,255,255,0.75)'  : 'rgba(15,23,42,0.7)';

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState(() => {
        const hasta = new Date();
        const desde = new Date();
        desde.setDate(desde.getDate() - 30);
        return [desde, hasta];
    });

    const cargar = async (desde, hasta) => {
        setLoading(true);
        try {
            const res = await aiAPI.obtenerMetricas({ desde, hasta });
            setData(res.data);
        } catch (err) {
            console.error('Error metricas:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dateRange && dateRange.length === 2) {
            cargar(formatLocalDate(dateRange[0]), formatLocalDate(dateRange[1]));
        } else {
            cargar();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDateChange = (range) => {
        setDateRange(range);
        if (range && range.length === 2) {
            cargar(formatLocalDate(range[0]), formatLocalDate(range[1]));
        } else {
            cargar();
        }
    };

    const formatNum = (n) => Number(n || 0).toLocaleString('es-PE');
    const formatCost = (n) => `$ ${Number(n || 0).toFixed(4)}`;
    const formatMs = (n) => `${Math.round(n || 0)}ms`;

    if (loading && !data) {
        return (
            <div className="card">
                <div className="loading">Cargando métricas...</div>
            </div>
        );
    }

    const k = data?.kpis || {};
    const apr = data?.aprobacion || {};

    // Label del rango activo para mostrar bajo el titulo (formato Dashboard).
    // Ej: "Últimos 30 días · 17 may → 16 jun"
    const formatRangoCorto = (rango) => {
        if (!rango || !rango[0] || !rango[1]) return '';
        const fmt = (d, withYear) => d.toLocaleDateString('es-PE', {
            day: '2-digit',
            month: 'short',
            ...(withYear ? { year: 'numeric' } : {})
        }).replace(/\./g, '');
        const mismoAnio = rango[0].getFullYear() === rango[1].getFullYear();
        return `${fmt(rango[0], !mismoAnio)} → ${fmt(rango[1], true)}`;
    };
    const periodoLabel = (() => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return 'Rango personalizado';
        const diffMs = dateRange[1].getTime() - dateRange[0].getTime();
        const dias = Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)));
        // "Últimos N días" solo si el rango termina hoy o ayer (tolerancia 1 día).
        // Si el rango esta en el pasado, mostrar el rango con duracion al final.
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const fin = new Date(dateRange[1]);
        fin.setHours(0, 0, 0, 0);
        const msDesdeHoy = hoy.getTime() - fin.getTime();
        const diasDesdeHoy = Math.round(msDesdeHoy / (1000 * 60 * 60 * 24));
        const terminaHoy = diasDesdeHoy >= 0 && diasDesdeHoy <= 1;
        return terminaHoy
            ? `Últimos ${dias} días · ${formatRangoCorto(dateRange)}`
            : `${formatRangoCorto(dateRange)} · ${dias} días`;
    })();
    const sinActividad = !loading && (Number(k.total_llamadas) || 0) === 0;

    return (
        <div className="card">
            <div className="card-header metricas-ia-header">
                <div className="flex flex-col">
                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Sparkles size={18} aria-hidden="true" />
                        Métricas de Uso de IA
                    </h3>
                    <span className="text-[11px] text-muted mt-0.5">{periodoLabel}</span>
                </div>
                <div className="metricas-ia-filtro">
                    <label className="metricas-ia-filtro-label">Filtrar por fecha</label>
                    <DateRangePicker
                        value={dateRange}
                        onChange={handleDateChange}
                        placeholder="Seleccionar rango"
                        placement="bottomEnd"
                    />
                </div>
            </div>

            <div className="admin-content">
                {/* Empty state global: sin llamadas a la IA en el rango seleccionado */}
                {sinActividad && (
                    <div className="metricas-empty">
                        <Sparkles size={20} className="metricas-empty-icon" aria-hidden="true" />
                        <div>
                            <div className="metricas-empty-title">Sin actividad de IA en este rango</div>
                            <div className="metricas-empty-sub">No se registraron llamadas entre {periodoLabel}. Prueba ampliando el rango con el filtro de fecha.</div>
                        </div>
                    </div>
                )}

                {/* KPIs principales — layout: icono + label en fila arriba, valor + sub debajo alineados al label */}
                <div className="metricas-kpis">
                    <div className="metrica-kpi">
                        <div className="metrica-kpi-head">
                            <div className="metrica-kpi-icon"><Activity size={18} /></div>
                            <div className="metrica-kpi-label">Llamadas totales</div>
                        </div>
                        <div className="metrica-kpi-body">
                            <div className="metrica-kpi-valor" title={formatNum(k.total_llamadas)}>{formatNumCompact(k.total_llamadas)}</div>
                        </div>
                    </div>

                    <div className="metrica-kpi">
                        <div className="metrica-kpi-head">
                            <div className="metrica-kpi-icon"><Zap size={18} /></div>
                            <div className="metrica-kpi-label">Tokens consumidos</div>
                        </div>
                        <div className="metrica-kpi-body">
                            <div className="metrica-kpi-valor" title={formatNum(k.tokens_total)}>{formatNumCompact(k.tokens_total)}</div>
                            <div className="metrica-kpi-sub">
                                {formatNumCompact(k.tokens_input_total)} in / {formatNumCompact(k.tokens_output_total)} out
                            </div>
                        </div>
                    </div>

                    <div className="metrica-kpi">
                        <div className="metrica-kpi-head">
                            <div className="metrica-kpi-icon"><DollarSign size={18} /></div>
                            <div className="metrica-kpi-label">Costo total (estim.)</div>
                        </div>
                        <div className="metrica-kpi-body">
                            <div className="metrica-kpi-valor">{formatCost(k.costo_total)}</div>
                            <div className="metrica-kpi-sub">según modelos usados</div>
                        </div>
                    </div>

                    <div className="metrica-kpi">
                        <div className="metrica-kpi-head">
                            <div className="metrica-kpi-icon"><Users size={18} /></div>
                            <div className="metrica-kpi-label">Usuarios activos</div>
                        </div>
                        <div className="metrica-kpi-body">
                            <div className="metrica-kpi-valor">{formatNum(k.usuarios_activos)}</div>
                        </div>
                    </div>

                    <div className="metrica-kpi">
                        <div className="metrica-kpi-head">
                            <div className="metrica-kpi-icon"><AlertCircle size={18} /></div>
                            <div className="metrica-kpi-label">Tasa de error</div>
                        </div>
                        <div className="metrica-kpi-body">
                            <div className={`metrica-kpi-valor ${k.tasa_error > 10 ? 'danger' : ''}`}>
                                {k.tasa_error || 0}%
                            </div>
                            <div className="metrica-kpi-sub">{formatNum(k.total_errores)} errores</div>
                        </div>
                    </div>

                    <div className="metrica-kpi">
                        <div className="metrica-kpi-head">
                            <div className="metrica-kpi-icon"><RefreshCw size={18} /></div>
                            <div className="metrica-kpi-label">Tasa de fallback</div>
                        </div>
                        <div className="metrica-kpi-body">
                            <div className={`metrica-kpi-valor ${k.tasa_fallback > 20 ? 'warning' : ''}`}>
                                {k.tasa_fallback || 0}%
                            </div>
                            <div className="metrica-kpi-sub">{formatNum(k.total_fallbacks)} con DeepSeek</div>
                        </div>
                    </div>

                    <div className="metrica-kpi">
                        <div className="metrica-kpi-head">
                            <div className="metrica-kpi-icon"><TrendingUp size={18} /></div>
                            <div className="metrica-kpi-label">Latencia promedio</div>
                        </div>
                        <div className="metrica-kpi-body">
                            <div className="metrica-kpi-valor">{formatMs(k.duracion_promedio_ms)}</div>
                        </div>
                    </div>
                </div>

                {/* Aprobacion de cotizaciones generadas con IA */}
                {apr.cotizaciones_generadas > 0 && (
                    <div className="metricas-section">
                        {/* h3 como flex para que el icono Target se alinee al texto */}
                        <h3 className="h3 metricas-section-title">
                            <Bot size={16} aria-hidden="true" />
                            Cotizaciones generadas con IA
                        </h3>
                        <div className="metricas-aprobacion">
                            <div className="metrica-aprob-item">
                                <span className="metrica-aprob-label">Generadas</span>
                                <span className="metrica-aprob-valor">{apr.cotizaciones_generadas}</span>
                                <span className="metrica-aprob-sub">Pasaron por el flujo de IA</span>
                            </div>
                            <div className="metrica-aprob-item">
                                <span className="metrica-aprob-label">Aprobadas</span>
                                <span className="metrica-aprob-valor success">{apr.aprobadas || 0}</span>
                                <span className="metrica-aprob-sub">Cerradas con éxito</span>
                            </div>
                            <div className="metrica-aprob-item">
                                <span className="metrica-aprob-label">Rechazadas</span>
                                <span className="metrica-aprob-valor danger">{apr.no_aprobadas || 0}</span>
                                <span className="metrica-aprob-sub">No avanzaron</span>
                            </div>
                            <div className="metrica-aprob-item destacado">
                                <span className="metrica-aprob-label">Tasa aprobación</span>
                                <span className={`metrica-aprob-valor ${apr.tasa_aprobacion >= 50 ? 'success' : 'warning'}`}>
                                    {apr.tasa_aprobacion}%
                                </span>
                                <span className="metrica-aprob-sub">
                                    {apr.cotizaciones_generadas > 0
                                        ? `${apr.aprobadas || 0} de ${apr.cotizaciones_generadas} generadas`
                                        : 'Aprobadas / Generadas'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tendencia diaria - grafico + tabla */}
                {data?.tendencia?.length > 0 && (() => {
                    const rows = data.tendenciaPorProveedor || [];
                    const fechasSet = new Set(rows.map(r => r.fecha.split('T')[0]));
                    data.tendencia.forEach(t => fechasSet.add(t.fecha.split('T')[0]));
                    const fechas = Array.from(fechasSet).sort();
                    const labels = fechas.map(f => {
                        const [, m, d] = f.split('-');
                        return `${d}/${m}`;
                    });
                    const seriesPara = (prov) => fechas.map(f => {
                        const r = rows.find(x => x.fecha.split('T')[0] === f && x.proveedor === prov);
                        return r ? r.total : 0;
                    });
                    const datasets = [];
                    // Paleta acorde Dashboard: azul (Gemini) + violeta (DeepSeek).
                    // Hues separados ~45° → distinguibles sin ser chillones.
                    if (rows.some(r => r.proveedor === 'gemini')) {
                        datasets.push({
                            label: 'Gemini',
                            data: seriesPara('gemini'),
                            borderColor: 'rgba(96, 165, 250, 1)',
                            backgroundColor: 'rgba(96, 165, 250, 0.15)',
                            borderWidth: 2.5,
                            fill: true,
                            tension: 0.35
                        });
                    }
                    if (rows.some(r => r.proveedor === 'deepseek')) {
                        datasets.push({
                            label: 'DeepSeek',
                            data: seriesPara('deepseek'),
                            borderColor: 'rgba(167, 139, 250, 1)',
                            backgroundColor: 'rgba(167, 139, 250, 0.15)',
                            borderWidth: 2.5,
                            borderDash: [6, 4],
                            fill: true,
                            tension: 0.35
                        });
                    }

                    return (
                        <div className="metricas-section">
                            <h3 className="h3">Tendencia diaria <span className="metricas-section-sub">llamadas a la IA por día, separadas por proveedor</span></h3>
                            <div className="metricas-tendencia-chart">
                                <Line
                                    data={{ labels, datasets }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        interaction: { mode: 'index', intersect: false },
                                        plugins: {
                                            legend: {
                                                position: 'top',
                                                align: 'end',
                                                labels: {
                                                    color: legendColor,
                                                    font: { size: 12 },
                                                    usePointStyle: true,
                                                    pointStyle: 'circle',
                                                    boxWidth: 8,
                                                    boxHeight: 8,
                                                    padding: 14
                                                }
                                            },
                                            tooltip: {
                                                backgroundColor: tooltipBg,
                                                titleColor: tooltipTitle,
                                                bodyColor: tooltipBody,
                                                borderColor: tooltipBorder,
                                                borderWidth: 1,
                                                padding: 10
                                            }
                                        },
                                        scales: {
                                            x: {
                                                grid: { color: gridColor },
                                                ticks: { color: axisColor, maxTicksLimit: 12, maxRotation: 0 }
                                            },
                                            y: {
                                                grid: { color: gridColor },
                                                ticks: { color: axisColor, precision: 0 },
                                                beginAtZero: true
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    );
                })()}

                {/* Por proveedor (Gemini vs DeepSeek) */}
                {data?.porProveedor?.length > 0 && (
                    <div className="metricas-section">
                        <h3 className="h3">Distribución por proveedor</h3>
                        <div className="metricas-proveedor-layout">
                            <div className="metricas-proveedor-chart">
                                <Doughnut
                                    data={{
                                        labels: data.porProveedor.map(p => p.proveedor === 'gemini' ? 'Gemini' : 'DeepSeek'),
                                        datasets: [{
                                            data: data.porProveedor.map(p => p.total),
                                            backgroundColor: data.porProveedor.map(p =>
                                                p.proveedor === 'gemini'
                                                    ? 'rgba(96, 165, 250, 0.7)'
                                                    : 'rgba(167, 139, 250, 0.7)'
                                            ),
                                            borderColor: data.porProveedor.map(p =>
                                                p.proveedor === 'gemini'
                                                    ? 'rgba(96, 165, 250, 1)'
                                                    : 'rgba(167, 139, 250, 1)'
                                            ),
                                            borderWidth: 2
                                        }]
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        cutout: '60%',
                                        plugins: {
                                            legend: {
                                                position: 'bottom',
                                                labels: {
                                                    color: legendColor,
                                                    font: { size: 12 },
                                                    usePointStyle: true,
                                                    pointStyle: 'circle',
                                                    boxWidth: 8,
                                                    boxHeight: 8,
                                                    padding: 12
                                                }
                                            },
                                            tooltip: {
                                                backgroundColor: tooltipBg,
                                                titleColor: tooltipTitle,
                                                bodyColor: tooltipBody,
                                                borderColor: tooltipBorder,
                                                borderWidth: 1,
                                                padding: 10,
                                                callbacks: {
                                                    label: (ctx) => {
                                                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                                                        const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                                                        return `${ctx.label}: ${ctx.parsed} llamadas (${pct}%)`;
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </div>
                            <div className="metricas-proveedores">
                                {data.porProveedor.map((p, i) => (
                                    <div key={i} className={`metrica-proveedor metrica-proveedor-${p.proveedor}`}>
                                        <div className="metrica-proveedor-nombre">
                                            {p.proveedor === 'gemini' ? 'Gemini' : 'DeepSeek'}
                                        </div>
                                        <div className="metrica-proveedor-stats">
                                            <div><strong>{formatNum(p.total)}</strong> llamadas</div>
                                            <div><strong>{formatNumCompact(p.tokens_input + p.tokens_output)}</strong> tokens</div>
                                            <div><strong>{formatCost(p.costo)}</strong> costo</div>
                                            <div><strong>{formatMs(p.latencia_promedio_ms)}</strong> latencia</div>
                                            {p.como_fallback > 0 && (
                                                <div className="meta">{p.como_fallback} usadas como fallback</div>
                                            )}
                                            {p.errores > 0 && (
                                                <div className="meta danger">{p.errores} errores</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Por tipo */}
                <div className="metricas-grid">
                    {data?.porTipo?.length > 0 && (
                        <div className="metricas-section">
                            <h3 className="h3">Llamadas por tipo</h3>
                            <div className="table-scroll">
                                <table className="galTable">
                                    <thead>
                                        <tr><th>Tipo</th><th>Total</th><th>Tokens</th></tr>
                                    </thead>
                                    <tbody>
                                        {data.porTipo.map((t, i) => (
                                            <tr key={i}>
                                                <td><span className={`badge tipo-${t.tipo}`}>{t.tipo}</span></td>
                                                <td>{formatNum(t.total)}</td>
                                                <td>{formatNum(t.tokens_total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {data?.porModelo?.length > 0 && (
                        <div className="metricas-section">
                            <h3 className="h3">Por modelo</h3>
                            <div className="table-scroll">
                                <table className="galTable">
                                    <thead>
                                        <tr><th>Modelo</th><th>Total</th><th>Tokens</th><th>Costo</th></tr>
                                    </thead>
                                    <tbody>
                                        {data.porModelo.map((m, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <code>{m.modelo}</code>
                                                    {' '}
                                                    <span className={`badge tipo-${m.proveedor}`}>{m.proveedor}</span>
                                                </td>
                                                <td>{formatNum(m.total)}</td>
                                                <td>{formatNum(m.tokens_total)}</td>
                                                <td>{formatCost(m.costo)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Top usuarios + Top prompts (2-col) */}
                <div className="metricas-grid">
                    {data?.topUsuarios?.length > 0 && (
                        <div className="metricas-section">
                            <h3 className="h3">Top usuarios</h3>
                            <div className="table-scroll">
                                <table className="galTable">
                                    <thead>
                                        <tr><th>Usuario</th><th>Rol</th><th>Llamadas</th><th>Tokens</th></tr>
                                    </thead>
                                    <tbody>
                                        {data.topUsuarios.map((u, i) => (
                                            <tr key={i}>
                                                <td>{u.nombre}</td>
                                                <td><span className="badge info">{u.rol}</span></td>
                                                <td>{formatNum(u.total)}</td>
                                                <td>{formatNum(u.tokens_total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {data?.topPrompts?.length > 0 && (
                        <div className="metricas-section">
                            <h3 className="h3">Top prompts más usados</h3>
                            <div className="metricas-prompts">
                                {data.topPrompts.map((p, i) => (
                                    <div key={i} className="metrica-prompt">
                                        <span className="metrica-prompt-veces">{p.veces}×</span>
                                        <span className="metrica-prompt-texto">{p.prompt_corto}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MetricasIA;
